import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminButton, AdminCard, AdminCardHeader, AdminPagination, AdminSearchInput, AdminStatusBadge, AdminTable, AdminTd, AdminTr } from "@/components/admin";
import type { AdminLocationRecord } from "../page";

interface LocationTableProps {
  records: AdminLocationRecord[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  totalCount: number;
  loading: boolean;
}

const riskTone: Record<AdminLocationRecord["riskLevel"], "emerald" | "orange" | "amber" | "slate"> = {
  normal: "emerald",
  warning: "orange",
  stale: "amber",
  empty: "slate",
};

const riskLabel: Record<AdminLocationRecord["riskLevel"], string> = {
  normal: "정상",
  warning: "확인 필요",
  stale: "지연",
  empty: "미수신",
};

const LocationTable = ({ records, searchQuery, onSearchChange, totalCount, loading }: LocationTableProps) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, totalCount]);

  const paged = useMemo(() => {
    const startIdx = (currentPage - 1) * perPage;
    return records.slice(startIdx, startIdx + perPage);
  }, [currentPage, records]);

  return (
    <AdminCard className="overflow-hidden">
      <AdminCardHeader
        title="위치 대상 목록"
        icon="ri-map-pin-line"
        action={(
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">총 {totalCount}명</span>
            <AdminSearchInput
              value={searchQuery}
              onChange={onSearchChange}
              placeholder="이름, 기기, 주소, ID 검색"
              className="w-64"
            />
          </div>
        )}
      />

      <AdminTable
        headers={["어르신", "최근 위치", "수집 방식", "권한", "위치 제공", "정확도", "수신 시각", "상태", "상세"]}
        loading={loading}
        emptyMessage={paged.length === 0 ? "조회 가능한 위치 대상이 없습니다." : undefined}
        colSpan={9}
      >
        {paged.map((row) => (
          <AdminTr key={row.id} className="hover:bg-slate-50/60">
            <AdminTd>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold text-slate-800">{row.elderlyName}</span>
                <span className="text-xs text-slate-400">
                  ID {row.seniorUserAccountId} · {row.deviceName || "기기 미연결"}
                </span>
              </div>
            </AdminTd>
            <AdminTd className="min-w-56">
              <span className="text-xs font-medium text-slate-700">{row.recentLocation}</span>
            </AdminTd>
            <AdminTd>
              <AdminStatusBadge label={row.collectionMethod} tone="slate" className="rounded text-xs" />
            </AdminTd>
            <AdminTd>
              <AdminStatusBadge
                label={row.permissionStatus || "확인 필요"}
                tone={row.permissionStatus === "GRANTED" ? "emerald" : "orange"}
                className="rounded text-xs"
              />
            </AdminTd>
            <AdminTd>
              <AdminStatusBadge
                label={row.providerStatus || "확인 필요"}
                tone={row.providerStatus === "ENABLED" ? "emerald" : "orange"}
                className="rounded text-xs"
              />
            </AdminTd>
            <AdminTd>
              <span className="text-xs text-slate-600">{row.accuracy}</span>
            </AdminTd>
            <AdminTd>
              <span className="text-xs text-slate-500">{row.receivedAt}</span>
            </AdminTd>
            <AdminTd>
              <AdminStatusBadge label={riskLabel[row.riskLevel]} tone={riskTone[row.riskLevel]} className="rounded text-xs" />
            </AdminTd>
            <AdminTd>
              <AdminButton variant="soft" size="sm" onClick={() => navigate(`/admin/location/${row.seniorUserAccountId}`)}>
                상세
              </AdminButton>
            </AdminTd>
          </AdminTr>
        ))}
      </AdminTable>

      <AdminPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        pageSize={perPage}
        itemLabel="명"
        onPageChange={setCurrentPage}
      />
    </AdminCard>
  );
};

export default LocationTable;
